import { useEffect, useState } from "react";
import { apiGet } from "../../lib/apiClient.js";
import { answerCheckState, awaitingAnswer } from "../../lib/forecast/answerCheck.js";

/** How often an open box re-reads the record, so a scheduled check that lands meanwhile shows. */
export const RECORD_REFRESH_MS = 10 * 60 * 1000;

/**
 * The search for an answer on a closed forecast: reads the resolver's latest check once the
 * question has closed with no answer, again after each "Check now" lands (the row's
 * `resolution.checked_at` moves), and every `RECORD_REFRESH_MS` while it waits.
 *
 * @param {{run: object|null, nowMs: number, enabled: boolean}} args `enabled`: forecast tracking
 * @returns {ReturnType<typeof answerCheckState>}
 */
export function useAnswerCheck({ run, nowMs, enabled }) {
  const waiting = enabled && awaitingAnswer(run, nowMs);
  const qkey = waiting ? run?.contractKey || null : null;
  const [round, setRound] = useState(0);
  useEffect(() => {
    if (!qkey) return undefined;
    const t = setInterval(() => setRound((n) => n + 1), RECORD_REFRESH_MS);
    return () => clearInterval(t);
  }, [qkey]);
  const key = qkey ? `${qkey}|${run?.resolution?.checked_at || ""}|${round}` : null;
  const [read, setRead] = useState({ qkey: null, attempt: null, failed: false });
  useEffect(() => {
    if (!key) return undefined;
    let cancelled = false;
    apiGet(`/api/resolve/report?qkey=${encodeURIComponent(qkey)}`).then((r) => {
      if (cancelled) return;
      // 204: no check recorded yet. Any other non-2xx: the record could not be read.
      const ok = r?.ok && r.body?.status;
      setRead({ qkey, attempt: ok ? r.body : null, failed: !ok && r?.status !== 204 });
    });
    return () => {
      cancelled = true;
    };
  }, [key, qkey]);
  if (!waiting) return null;
  // Nothing until the first read answers, so "not checked yet" never flashes before the record.
  // A re-read after a check keeps showing the previous record meanwhile.
  if (qkey && read.qkey !== qkey) return null;
  return answerCheckState(run, {
    attempt: qkey ? read.attempt : null,
    // A forecast with no contract key has no record to read.
    readFailed: !qkey || read.failed,
    nowMs,
  });
}
